<template>
  <div class="flex-start">
    <div class="compound-svg">
      <h3 class="mb">{{ tn(`passwordManager`) }}</h3>
      <div v-if="displayAccountsUsed" class="mb">{{ tn(`accountsUsed`, { used: seatsUsed, total: totalSeats }) }}</div>
      <div>
        <button
          v-if="displayAdditionalAccounts"
          id="btnLastPassAdd"
          class="inline-btn-link mt"
          @click="openLastPassModal('Add')"
        >
          {{ tn(`accountSetup`) }}
        </button>
      </div>
      <div v-if="displayActivate">
        <p class="mb">{{ tn(`activateMessage`) }}</p>
        <button
          id="btnLastPassActivate"
          class="btn-outline-primary"
          :disabled="disableActivate"
          @click="openLastPassModal('Activate')"
        >
          {{ tn(`activate`) }}
        </button>
      </div>
      <div v-if="displayManageAccounts">
        <button id="btnLastPassManage" class="btn-outline-primary" @click="manageAccounts()">
          {{ tn(`managePasswords`) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, PropType, computed, onBeforeMount } from "vue";
import { logEvent } from "@/common/logger";
import { t } from "@/i18n";
import { ILastPassInfo, IWebrootSubscriptions } from "@/common/api/unifiedPortal/interfaces";
import { getUrl } from "@/common/getUrl";

const componentName = "LastPassSecurityTabComponent";

export default defineComponent({
  name: componentName,
  props: {
    lastPassInfo: {
      type: Object as PropType<ILastPassInfo>,
      default: null,
    },
    webrootSubInfo: {
      type: Object as PropType<IWebrootSubscriptions | undefined>,
      required: true,
    },
  },
  emits: ["openLastPassModal"],
  setup(props, context) {
    logEvent("Created", componentName);
    const displayActivate = ref<boolean>(false);
    const displayAdditionalAccounts = ref<boolean>(false);
    const displayManageAccounts = ref<boolean>(true);
    const displayAccountsUsed = ref<boolean>(false);
    const showLastPassModal = ref<boolean>(false);
    const modalOperation = ref<string>("");
    const seatsUsed = ref<number>(0);
    const totalSeats = ref<number>(0);

    onBeforeMount(() => {
      render();
    });

    const disableActivate = computed(
      () => !props.webrootSubInfo?.isActive || props.webrootSubInfo?.licenseStatusDescription === "Inactive"
    );

    function render() {
      const info = props.lastPassInfo;
      if (info) {
        // Get Pending activations
        seatsUsed.value = info.seatsUsed;
        totalSeats.value = info.seatsAvailable + info.seatsUsed;
        const hasPendingURL = info.pendingLastPassTokenUrl.length > 0;
        const hasCompletedAccounts =
          (info.seatsUsed > 0 && info.pendingLastPassTokenUrl.length < 1) || info.seatsUsed > 1;
        const hasSeatsAvailable = info.seatsAvailable > 0;

        logEvent("Load info", componentName, info);
        // display Activate button if no seat was used;
        displayActivate.value = seatsUsed.value == 0;
        // display Additional Accounts button if there is still seats available;
        displayAdditionalAccounts.value = !displayActivate.value && (hasSeatsAvailable || hasPendingURL);
        // display Manage Accounts button if there are
        displayManageAccounts.value = hasCompletedAccounts;
      } else if (props.webrootSubInfo) {
        totalSeats.value = props.webrootSubInfo.seats;
        seatsUsed.value = props.webrootSubInfo.seats;
      }
      displayAccountsUsed.value = !displayActivate.value && seatsUsed.value !== totalSeats.value;
    }

    function manageAccounts() {
      window.open(getUrl("LASTPASS_PORTAL_URL"));
    }

    function completeSignUp(url: string) {
      window.open(url);
    }

    const getAdditionalAccountsClass = computed(() => {
      return displayManageAccounts.value ? "btn-outline-primary" : "btn-primary";
    });

    // LastPass Modal
    function openLastPassModal(operation: string) {
      context.emit("openLastPassModal", operation);
    }

    function tn(v: string, params?: Record<string, unknown>): string {
      return t(`${componentName}.${v}`, params);
    }

    //Expose to template
    return {
      t,
      tn,
      getAdditionalAccountsClass,
      manageAccounts,
      completeSignUp,
      modalOperation,
      showLastPassModal,
      displayActivate,
      displayAdditionalAccounts,
      displayManageAccounts,
      openLastPassModal,
      seatsUsed,
      totalSeats,
      displayAccountsUsed,
      disableActivate,
    };
  },
});
</script>
